import React, { useContext } from 'react';
import i18next from 'core/i18n';
import { AgFinancingCollateralAnalysisStatus, Collateral, Phase2 } from '@indigo-ag/schema';
import { Body, palettes, RegularTitle, SmallTitle, TinyBody, TinyTitle } from '@indigo-ag/component-module';

import {
  NotificationSection,
  NotificationSectionStatus
} from '../../../../shared/NotificationSection/NotificationSection';
import { UserContext } from 'core/components/GateWay/GateWay';
import {
  Permissions,
  CollateralUtil,
  UserUtil,
  EnhancedExpansionPanel,
  createStylesHook,
  currency,
  numberWithDots,
  date,
  CURRENCY_SYMBOL,
  parseNumberToLocale,
  GridContainerItem,
  GridContainer,
  utils
} from 'shared';
import { MatriculaAnalysisDetails } from '../Analysis/MatriculaAnalysisDetails';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import { PendenciesList } from 'features/brazil-grower/components/BrazilGrowerFinance/shared/PendenciesSection/PendenciesList/PendenciesList';
import { getFreeArea } from '../Analysis/utils';

const collateralNotificationStateTitles: { [key in AgFinancingCollateralAnalysisStatus]?: string } = {
  [AgFinancingCollateralAnalysisStatus.COMPLETED]: i18next.t('completed'),
  [AgFinancingCollateralAnalysisStatus.STARTED]: i18next.t('in_process'),
  [AgFinancingCollateralAnalysisStatus.ON_HOLD]: i18next.t('onHold'),
  // * deprecated
  [AgFinancingCollateralAnalysisStatus.APPROVED]: i18next.t('completed'),
  [AgFinancingCollateralAnalysisStatus.DENIED]: i18next.t('denied'),
  [AgFinancingCollateralAnalysisStatus.WAITING_CREDIT_MANAGER_DECISION]: i18next.t('sentToCommittee')
};

interface CollateralAnalysisStateProps {
  collateralInfo: Collateral;
  matriculas: Phase2['matriculas'];
}

const styles = createStylesHook(({ typography: { pxToRem } }) => ({
  titleCollateralData: {
    color: palettes.brand.gray['700'],
    fontSize: pxToRem(13),
    margin: '8px 0'
  }
}));

export const CollateralAnalysisState: React.FunctionComponent<CollateralAnalysisStateProps> = ({
  collateralInfo,
  matriculas
}) => {
  const {
    notes,
    status,
    completedAt,
    completedBy,
    decisionChangedAt,
    decisionChangedBy,
    decisionNotes,
    startedAt,
    startedBy
  } = collateralInfo;

  /**
   * styles
   */
  const classes = styles();

  /**
   * User Context
   */
  const { hasPermission } = useContext(UserContext);
  const isLuchesi = hasPermission(Permissions.FA_EXTERNAL_LEGAL);
  const isCreditLead = hasPermission(Permissions.FA_CREDIT_LEAD);
  const isInternal = hasPermission(Permissions.FA_INTERNAL) || hasPermission(Permissions.FA_ADMIN);

  /**
   * Finance Process Context
   */
  const { agFinance } = useContext(FinanceProcessContext);

  const getNotificationUpdatedAt = () => {
    return CollateralUtil.hasBeenSent(status)
      ? completedAt
      : CollateralUtil.hasNotBeenSent(status)
      ? startedAt
      : decisionChangedAt;
  };

  const getNotificationUpdatedBy = () => {
    return CollateralUtil.hasBeenSent(status)
      ? completedBy?.firstName
      : CollateralUtil.hasNotBeenSent(status)
      ? startedBy
      : UserUtil.getUserFullName(decisionChangedBy);
  };

  const getNotificationStatus = (): NotificationSectionStatus => {
    return CollateralUtil.hasNotBeenSent(status) || CollateralUtil.isOnHold(status)
      ? 'warning'
      : CollateralUtil.hasBeenSent(status) || CollateralUtil.hasBeenApproved(status)
      ? 'success'
      : 'error';
  };

  const opportunity = agFinance?.orderInfo?.opportunities[0];
  const totals = getFreeArea(matriculas);
  const cprSentAt = agFinance?.phase3?.draftSentAt;

  const negotiationDataGridItems: GridContainerItem[] = [
    {
      label: i18next.t('contractValue'),
      value: currency.inputMaskCurrency(opportunity?.contractValue, {
        useComaForDecimals: true,
        currencySymbol: CURRENCY_SYMBOL.BR
      })
    },
    {
      label: i18next.t('valuePercentage', { percentage: '130%' }),
      value: currency.inputMaskCurrency(opportunity?.collateralValue, { useComaForDecimals: true })
    },
    {
      label: i18next.t('bagsPriceBase'),
      value: parseNumberToLocale(opportunity?.pricePerBag)
    },
    {
      label: i18next.t('bagsPercentage', { percentage: '100%' }),
      value: parseNumberToLocale(Math.ceil(opportunity?.collateralBags || 0))
    },
    {
      label: i18next.t('bagsPercentage', { percentage: '130%' }),
      value: parseNumberToLocale(Math.ceil(opportunity?.collateralBags130 || 0))
    },
    {
      label: i18next.t('productivityAverage'),
      value: parseNumberToLocale(opportunity?.bagsPerHectares)
    },
    {
      label: utils.text.toTitleCase(i18next.t('pledgeArea')),
      value: opportunity?.collateralHectares ? `${parseNumberToLocale(opportunity?.collateralHectares)} Ha` : undefined
    },
    {
      label: i18next.t('totalFreeAreaInput'),
      value: numberWithDots(totals.cultivableArea)
    },
    {
      label: i18next.t('percentFreeAnalysis'),
      value: numberWithDots(totals.percentageArea)
    },
    {
      label: i18next.t('cprSent'),
      value: date.format({ date: cprSentAt })
    }
  ];

  return (
    <NotificationSection
      status={getNotificationStatus()}
      title={collateralNotificationStateTitles[status] || ''}
      updatedAt={getNotificationUpdatedAt()}
      updateBy={getNotificationUpdatedBy()}
    >
      {decisionNotes && (
        <>
          <TinyTitle className="mt-2 mb-1 text-transform-capitalize" style={{ color: palettes.brand.gray[700] }}>
            {i18next.t('decisionNotes')}
          </TinyTitle>
          <Body className="mt-1 mb-1 break-line">{decisionNotes}</Body>
        </>
      )}
      {notes && (
        <>
          <TinyBody className={`${classes.titleCollateralData}`}>{i18next.t('commentsOrRecommendations')}</TinyBody>
          <Body className="mb-3 mt-0 break-line">{notes}</Body>
        </>
      )}
      {(isCreditLead || isInternal) && (
        <>
          <RegularTitle className="mb-3">{i18next.t('negotiationData')}</RegularTitle>
          <GridContainer columns={3} items={negotiationDataGridItems} />
        </>
      )}
      {!isLuchesi && (
        <>
          <div className="mt-4">
            <EnhancedExpansionPanel title={<SmallTitle>{i18next.t('matriculaAnalysis')}</SmallTitle>}>
              <MatriculaAnalysisDetails
                analysisInfo={{
                  matriculas: matriculas?.filter(m => m.includedInAnalysis)
                }}
              />
            </EnhancedExpansionPanel>
            {collateralInfo.pendencies?.length ? (
              <EnhancedExpansionPanel title={<SmallTitle>{i18next.t('pendencies')}</SmallTitle>}>
                <PendenciesList pendencies={collateralInfo.pendencies} readonly />
              </EnhancedExpansionPanel>
            ) : null}
          </div>
        </>
      )}
    </NotificationSection>
  );
};
